import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomModal from "./CustomModal";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast } from "../../utils/ShowToast";
import { GetItemQtyLogsApi } from "../../ApiRequests";
import { getAppBasePath } from "../../utils/appPaths";
import moment from "moment";
import { formatDateTime } from "../../utils/dateTime";

const ItemQtyLogModal = ({ Open, setOpen, product }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!Open || !product?._id) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await GetItemQtyLogsApi(product._id);
        const payload = res?.data?.data?.payload;
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) setLogs(list);
      } catch (e) {
        if (!cancelled) {
          setLogs([]);
          ErrorToast(
            e.response?.data?.error?.msg || "Could not load adjustment log"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [Open, product?._id]);

  if (!product?._id) return null;

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-6 max-h-[85vh] overflow-y-auto min-w-[min(100vw-2rem,720px)]">
        <h1 className="w-full text-center font-[700] text-2xl py-5 px-4">
          Quantity adjustment log
        </h1>
        <p className="text-center text-sm text-inkSecondary px-6 mb-3">
          {product.name} ({product.code})
        </p>
        {loading ? (
          <div className="flex justify-center py-10">
            <AddingLightLoader />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center text-muted py-8 px-4">
            No manual adjustments recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto px-4">
            <table className="w-full text-sm border-collapse border border-line">
              <thead>
                <tr className="bg-main text-white">
                  <th className="border border-line px-2 py-2 text-left">When</th>
                  <th className="border border-line px-2 py-2">Δ</th>
                  <th className="border border-line px-2 py-2">Qty before</th>
                  <th className="border border-line px-2 py-2">Qty after</th>
                  <th className="border border-line px-2 py-2">In before</th>
                  <th className="border border-line px-2 py-2">In after</th>
                  <th className="border border-line px-2 py-2 text-left">
                    Reason
                  </th>
                  <th className="border border-line px-2 py-2 text-left">By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row) => (
                  <tr key={row._id} className="font-quicksand">
                    <td className="border border-line px-2 py-1.5 whitespace-nowrap">
                      {row.createdAt
                        ? formatDateTime(row.createdAt)
                        : "—"}
                    </td>
                    <td
                      className={`border border-line px-2 py-1.5 text-center font-semibold ${
                        row.delta > 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {row.delta > 0 ? `+${row.delta}` : row.delta}
                    </td>
                    <td className="border border-line px-2 py-1.5 text-center">
                      {row.previousQty}
                    </td>
                    <td className="border border-line px-2 py-1.5 text-center">
                      {row.newQty}
                    </td>
                    <td className="border border-line px-2 py-1.5 text-center">
                      {row.previousInQty != null ? row.previousInQty : "—"}
                    </td>
                    <td className="border border-line px-2 py-1.5 text-center">
                      {row.newInQty != null ? row.newInQty : "—"}
                    </td>
                    <td className="border border-line px-2 py-1.5 max-w-[220px]">
                      {row.reason}
                    </td>
                    <td className="border border-line px-2 py-1.5 text-xs">
                      {row.performedByName || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-center gap-2 mt-5 flex-wrap px-4">
          <button
            type="button"
            className="px-5 py-2 border-2 border-main bg-main text-white rounded-lg font-bold hover:bg-mainHover transition-colors active:scale-[.98]"
            onClick={() => {
              setOpen(false);
              navigate(
                `${getAppBasePath()}/stock-qty-log/item/${product._id}`
              );
            }}
          >
            Full report
          </button>
          <button
            type="button"
            className="px-5 py-2 border-2 border-main bg-brandSoft text-main rounded-lg font-bold hover:bg-main hover:text-white transition-colors active:scale-[.98]"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ItemQtyLogModal;
